/**
 * RNR's Collapsible — a disclosure that folds open IN PLACE.
 *
 * Two of them in the app, and both are the same shape: a one-line trigger with the answer under
 * it. "How this helps" on a revealed Sync round, and the folded terms row on Wrapped's give form.
 * Neither is a modal and neither navigates — the thing you were reading stays where it was and
 * grows, which is the whole reason a disclosure is the right control for a detail nobody has to
 * read.
 *
 * The primitive is what earns the swap from a `useState` boolean: it puts `expanded` on the
 * trigger's accessibility state, so a screen reader says "collapsed"/"expanded" rather than
 * reading a caret glyph, and it keeps the open state and the trigger in one place instead of two.
 * `Content` renders nothing when closed rather than hiding it, so a folded panel costs no layout
 * and its inputs are genuinely absent.
 */
import { cn } from '@/lib/utils';
import * as CollapsiblePrimitive from '@rn-primitives/collapsible';
import { Platform } from 'react-native';

function Collapsible({ className, ...props }: React.ComponentProps<typeof CollapsiblePrimitive.Root>) {
  return <CollapsiblePrimitive.Root className={cn('gap-2', className)} {...props} />;
}

function CollapsibleTrigger({ className, ...props }: React.ComponentProps<typeof CollapsiblePrimitive.Trigger>) {
  return (
    <CollapsiblePrimitive.Trigger
      className={cn(
        'flex-row items-center gap-1.5 self-start',
        Platform.select({ web: 'cursor-default disabled:pointer-events-none' }),
        props.disabled && 'opacity-50',
        className,
      )}
      {...props}
    />
  );
}

function CollapsibleContent({ className, ...props }: React.ComponentProps<typeof CollapsiblePrimitive.Content>) {
  return <CollapsiblePrimitive.Content className={cn('gap-2', className)} {...props} />;
}

export { Collapsible, CollapsibleContent, CollapsibleTrigger };
